// A network list's entries (D81): Squid `dstdomain` form, the same rules the API applies. A
// bare name is that host; a leading dot is the domain and every subdomain; `*.` is accepted
// and stored as the dot form. Lower-case, at most 253 characters, at most 1000 per list.
export const MAX_DOMAINS = 1000;
const MAX_LENGTH = 253;
const LABEL = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;

export type DomainCheck = { ok: true; value: string } | { ok: false; input: string };

/** One entry, normalised: trimmed, lower-case, `*.x` → `.x`; refused when it is not a domain. */
export function normaliseDomain(input: string): DomainCheck {
  let value = input.trim().toLowerCase();
  if (value.startsWith("*.")) value = value.slice(1);
  const name = value.startsWith(".") ? value.slice(1) : value;
  const labels = name.split(".");
  const ok =
    value.length > 0 &&
    value.length <= MAX_LENGTH &&
    labels.length >= 2 &&
    labels.every((l) => LABEL.test(l)) &&
    /[a-z]/.test(labels[labels.length - 1]!);
  return ok ? { ok: true, value } : { ok: false, input: input.trim() };
}

export interface ParsedDomains {
  /** Valid, normalised, in first-seen order, without repeats. */
  valid: string[];
  invalid: string[];
}

/**
 * Entries typed or uploaded: one per line, `#` starts a comment, repeats are dropped. Typed
 * text (and a `.txt`) may also separate entries with commas or spaces; a `.csv` counts each row
 * by its first column, as the API's import does.
 */
export function parseDomains(text: string, format: "list" | "csv" = "list"): ParsedDomains {
  const valid: string[] = [];
  const invalid: string[] = [];
  for (const raw of text.split(/\r?\n/)) {
    const line = raw.replace(/#.*$/, "").trim();
    if (line === "") continue;
    const cells = format === "csv" ? [line.split(",")[0]!] : line.split(/[\s,;]+/);
    for (const cell of cells) {
      const entry = cell.trim().replace(/^["']|["']$/g, "");
      if (entry === "") continue;
      const check = normaliseDomain(entry);
      if (!check.ok) invalid.push(check.input);
      else if (!valid.includes(check.value)) valid.push(check.value);
    }
  }
  return { valid, invalid };
}

/** Whether `host` is matched by `entry` the way Squid's dstdomain matches it. */
export function covers(entry: string, host: string): boolean {
  return entry.startsWith(".") ? host === entry.slice(1) || host.endsWith(entry) : host === entry;
}
